import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import { characterClassCardApi as cardApi } from './characterClass.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';
import { setRarities, setSkins, setSizes } from './components/CharacterClassSkin/characterClassSkinSlice';

export const fetchCharacterClassCardApi = createAsyncThunk(
    'cardSaga/characterClassCard/fetchCharacterClassCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleCharacterClassCardPublishedStateApi = createAsyncThunk(
    'cardSaga/characterClassCard/toggleCharacterClassCardPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);
            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createCharacterClassCardApi = createAsyncThunk(
    'cardSaga/characterClassCard/createCharacterClassCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeCharacterClassCardApi = createAsyncThunk(
    'cardSaga/characterClassCard/storeCharacterClassCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            const { model } = await cardApi.store(values);

            // return alertSwal('success', 'Card generated successfully!');
            navigate(`/character-class-card/update/${model.id}`, { state: { targetId: 'skinAddedForm' } });
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const showCharacterClassCardApi = createAsyncThunk(
    'cardSaga/characterClassCard/showCharacterClassCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await cardApi.show(id);

            dispatch(setSkins(data.skins));
            dispatch(setRarities(data.rarities));
            dispatch(setSizes(data.skinSizes));

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateCharacterClassCardApi = createAsyncThunk(
    'cardSaga/characterClassCard/updateCharacterClassCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values, formikBag } = params;
        try {
            dispatch(requestStarted());
            await cardApi.update(id, values);
            alertSwal('success', 'Card updated successfully!');

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        } finally {
            dispatch(requestFinished());
            formikBag.setSubmitting(false);
        }
    },
);

export const deleteCharacterClassCardItemApi = createAsyncThunk(
    'cardSaga/characterClassCard/deleteCharacterClassCardItemApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await cardApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const characterClassCardSlice = createSlice({
    name: 'cardSaga/characterClassCard',
    initialState: {
        rows: [],
        emptyModel: {},
        skins: [],
        rarities: [],
        editedModel: {},
        allPictures: [],
        characterAndCardPictures: [],
        bonusConditions: [],
    },
    reducers: {
        clearEditedCharacterClassCard(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCharacterClassCardApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchCharacterClassCardApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleCharacterClassCardPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleCharacterClassCardPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createCharacterClassCardApi.fulfilled, (state, { payload }) => {
                state.emptyModel = payload.model;
                state.characterAndCardPictures = payload.characterAndCardPictures;
                state.allPictures = payload.allPictures;
                state.bonusConditions = payload.bonusConditions;
            })
            .addCase(createCharacterClassCardApi.rejected, () => {
                //
            });

        builder
            .addCase(storeCharacterClassCardApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeCharacterClassCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showCharacterClassCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.skins = payload.skins;
                state.rarities = payload.rarities;
                state.characterAndCardPictures = payload.characterAndCardPictures;
                state.allPictures = payload.allPictures;
                state.bonusConditions = payload.bonusConditions;
            })
            .addCase(showCharacterClassCardApi.rejected, () => {
                //
            });

        builder
            .addCase(updateCharacterClassCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make ActionUpdate re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
            })
            .addCase(updateCharacterClassCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteCharacterClassCardItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedCharacterClassCard } = characterClassCardSlice.actions;
